import LiquidText from '../components/LiquidText';
import { OrcamentoIcon, OrcamentoDialog, OrcamentoFeedbackProvider, useOrcamentoFeedback } from '../components/OrcamentoUI';
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './Utilizadores.css';

const UtilizadoresContent = () => {
    const { confirm, notify } = useOrcamentoFeedback();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('interno');
    const [filtroEstado, setFiltroEstado] = useState('ativos'); // 'ativos' | 'inativos' | 'todos'
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const ALL_MODULES = [
        { key: 'dashboard', label: 'Dashboard' },
        { key: 'obras', label: 'Obras' },
        { key: 'trabalhadores', label: 'Trabalhadores' },
        { key: 'recursos-humanos', label: 'Recursos Humanos' },
        { key: 'equipamentos', label: 'Equipamentos' },
        { key: 'veiculos', label: 'Veiculos' },
        { key: 'planeamento', label: 'Planeamento' },
        { key: 'manutencoes', label: 'Manutenção' },
        { key: 'maquetes', label: 'Maquetes' },
        { key: 'orcamentos', label: 'Orcamentos' },
        { key: 'gestao-financeira', label: 'Gestao Financeira' },
        { key: 'despesas', label: 'Despesas' },
        { key: 'relatorios', label: 'Relatorios' },
        { key: 'sistema-integrado', label: 'Sistema Integrado' },
        { key: 'notificacoes', label: 'Notificacoes' },
    ];

    const [formData, setFormData] = useState({
        username: '',
        password: '',
        nome_completo: '',
        email: '',
        telefone: '',
        role: 'user',
        perfil_mobile: false,
        tipo_utilizador: 'interno',
        permissoes: null
    });
    const [showPassword, setShowPassword] = useState(false);
    const [sendingRecovery, setSendingRecovery] = useState(null);
    const hasRecoveryEmail = user => typeof user.email === 'string' && user.email.trim().length <= 254 && /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/.test(user.email.trim());
    // Mesma regra do backend (utils/doisPassos.js): os mobile entram só com a palavra-passe.
    const exigeDoisPassos = user => (user.tipo_utilizador || 'interno') === 'interno' && !user.perfil_mobile;
    const [aRepor2fa, setARepor2fa] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await api.get('/auth/users');
            setUsers(res.data);
        } catch (error) {
            console.error('Erro ao buscar utilizadores:', error);
        } finally {
            setLoading(false);
        }
    };

    const usersDoSeparador = users.filter(u => {
        const type = u.tipo_utilizador || 'interno';
        if (activeTab === 'interno') return type === 'interno' || type === 'mobile';
        return type === 'externo';
    });
    const contagemEstado = {
        ativos: usersDoSeparador.filter(u => u.ativo).length,
        inativos: usersDoSeparador.filter(u => !u.ativo).length,
        todos: usersDoSeparador.length
    };
    const filteredUsers = usersDoSeparador.filter(u =>
        filtroEstado === 'todos' || (filtroEstado === 'ativos' ? u.ativo : !u.ativo));

    const handleOpenModal = (user = null) => {
        if (user) {
            setEditingUser(user);
            setFormData({
                username: user.username,
                password: '',
                nome_completo: user.nome_completo,
                email: user.email,
                telefone: user.telefone || '',
                role: user.role,
                perfil_mobile: user.perfil_mobile || false,
                tipo_utilizador: user.tipo_utilizador || 'interno',
                permissoes: user.permissoes || null
            });
        } else {
            setEditingUser(null);
            setFormData({
                username: '',
                password: '',
                nome_completo: '',
                email: '',
                telefone: '',
                role: 'user',
                perfil_mobile: false,
                tipo_utilizador: 'interno',
                permissoes: null
            });
        }
        setShowPassword(false);
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingUser) {
                await api.put(`/auth/users/${editingUser.id}`, formData);
            } else {
                await api.post('/auth/users', formData);
            }
            setShowModal(false);
            fetchUsers();
            await notify('Utilizador guardado com sucesso!');
        } catch (error) {
            console.error('Erro ao guardar utilizador:', error);
            await notify('Erro ao guardar utilizador');
        }
    };

    const sendRecovery = async user => {
        if (!await confirm(`Enviar uma ligação de recuperação da palavra-passe para ${user.email}?`)) return;
        setSendingRecovery(user.id);
        try {
            const response = await api.post(`/auth/users/${user.id}/password-reset`, {}, { timeout: 45000 });
            await notify(response.data.message);
        } catch (error) {
            await notify(error.response?.data?.message || 'Não foi possível enviar o email de recuperação.');
        } finally {
            setSendingRecovery(null);
        }
    };

    const repor2fa = async user => {
        if (!await confirm(`Repor a verificação em dois passos de ${user.nome_completo}?\n\nA app autenticadora atual deixa de funcionar e, no próximo login, terá de ler um QR novo.`)) return;
        setARepor2fa(user.id);
        try {
            const response = await api.post(`/auth/users/${user.id}/dois-passos/repor`);
            fetchUsers();
            await notify(response.data.message);
        } catch (error) {
            await notify(error.response?.data?.message || 'Não foi possível repor a verificação.');
        } finally {
            setARepor2fa(null);
        }
    };

    const toggleStatus = async (user) => {
        try {
            await api.put(`/auth/users/${user.id}`, { ...user, ativo: !user.ativo });
            fetchUsers();
        } catch (error) {
            console.error('Erro ao alterar status:', error);
        }
    };

    const handleDelete = async (user) => {
        if (!await confirm(`Tem a certeza que deseja apagar permanentemente o utilizador "${user.nome_completo}"?\n\nEsta ação não pode ser revertida.`)) {
            return;
        }
        try {
            await api.delete(`/auth/users/${user.id}`);
            fetchUsers();
            await notify('Utilizador eliminado com sucesso!');
        } catch (error) {
            console.error('Erro ao eliminar utilizador:', error);
            await notify('Erro ao eliminar utilizador. Verifique se não existem obras ou despesas associadas.');
        }
    };

    if (loading) return <div className="p-8">A carregar utilizadores...</div>;

    return (
        <div className="users-page admin-theme-page">
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1><OrcamentoIcon name="users" fallback="👥" /> Gestão de Utilizadores</h1>
                        <p>Controle de acesso ao portal</p>
                    </div>
                </div>
                <div className="top-bar-actions">
                    <button className="btn-primary" onClick={() => handleOpenModal()}><OrcamentoIcon name="plus" fallback="+" /> Novo Utilizador</button>
                </div>
            </div>

            <div className="page-content">
                <div className="users-toolbar">
                    <div className="shared-subnav" role="group" aria-label="Tipo de utilizador">
                        <button className={`shared-subnav-btn ${activeTab === 'interno' ? 'active' : ''}`} aria-pressed={activeTab === 'interno'} onClick={() => setActiveTab('interno')}>Utilizadores Internos</button>
                        <button className={`shared-subnav-btn ${activeTab === 'externo' ? 'active' : ''}`} aria-pressed={activeTab === 'externo'} onClick={() => setActiveTab('externo')}>Utilizadores Externos</button>
                    </div>
                    <div className="shared-subnav" role="group" aria-label="Estado">
                        {[['ativos', 'Ativos'], ['inativos', 'Inativos'], ['todos', 'Todos']].map(([valor, rotulo]) => (
                            <button key={valor} className={`shared-subnav-btn ${filtroEstado === valor ? 'active' : ''}`} aria-pressed={filtroEstado === valor}
                                onClick={() => setFiltroEstado(valor)}>
                                {rotulo} <span className="users-count">{contagemEstado[valor]}</span>
                            </button>
                        ))}
                    </div>
                </div>
                <div className="users-table-container">
                    <table className="users-table standard-table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Username</th>
                                <th>Email</th>
                                <th>Telefone</th>
                                <th>Tipo</th>
                                <th>Role</th>
                                <th>Último Acesso</th>
                                <th>Estado</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.length === 0 && (
                                <tr><td colSpan={9} className="users-empty">
                                    {filtroEstado === 'inativos' ? 'Não há utilizadores inativos.' : filtroEstado === 'ativos' ? 'Não há utilizadores ativos.' : 'Não há utilizadores.'}
                                </td></tr>
                            )}
                            {filteredUsers.map(u => (
                                <tr key={u.id} className={u.ativo ? '' : 'inactive-row'}>
                                    <td><strong>{u.nome_completo}</strong></td>
                                    <td>{u.username}</td>
                                    <td>{u.email || '—'}
                                        {u.tipo_utilizador !== 'externo' && !hasRecoveryEmail(u) && <small style={{ display: 'block', color: '#975a16' }}>Sem email para recuperação</small>}
                                    </td>
                                    <td>{u.telefone}</td>
                                    <td>
                                        <span
                                            className={`role-badge ${u.tipo_utilizador === 'externo' ? 'viewer' : 'admin'}`}
                                            style={{
                                                background: u.tipo_utilizador === 'externo' ? '#e2e8f0' : (u.tipo_utilizador === 'mobile' ? '#f0fff4' : '#ebf8ff'),
                                                color: u.tipo_utilizador === 'externo' ? '#4a5568' : (u.tipo_utilizador === 'mobile' ? '#2f855a' : '#2b6cb0')
                                            }}
                                        >
                                            {u.tipo_utilizador === 'externo' ? 'EXTERNO' : (u.tipo_utilizador === 'mobile' ? 'MOBILE' : 'INTERNO')}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                            <span className={`role-badge ${u.role}`}>
                                                {u.role}
                                            </span>
                                            {u.perfil_mobile && <span className="role-badge" style={{ background: '#bee3f8', color: '#2b6cb0' }}>Mobile</span>}
                                        </div>
                                    </td>
                                    <td>{u.ultimo_acesso ? new Date(u.ultimo_acesso).toLocaleString() : 'Nunca'}</td>
                                    <td>
                                        <span className={`status-dot ${u.ativo ? 'active' : 'inactive'}`}></span>
                                        {u.ativo ? 'Ativo' : 'Inativo'}
                                        {exigeDoisPassos(u) && <small style={{ display: 'block', color: u.totp_ativo ? '#2f855a' : '#975a16' }}>
                                            {u.totp_ativo ? 'App autenticadora configurada' : 'App autenticadora por configurar'}
                                        </small>}
                                    </td>
                                    <td className="actions-cell">
                                        {u.tipo_utilizador !== 'externo' && <button
                                            type="button"
                                            className="btn-icon"
                                            title={u.ativo && hasRecoveryEmail(u) ? 'Enviar email de recuperação de palavra-passe' : 'Requer uma conta ativa com email válido'}
                                            aria-label={`Recuperar palavra-passe de ${u.nome_completo}`}
                                            disabled={!u.ativo || !hasRecoveryEmail(u) || sendingRecovery !== null}
                                            onClick={() => sendRecovery(u)}
                                        ><OrcamentoIcon name="mail" fallback="✉" /></button>}
                                        {exigeDoisPassos(u) && <button
                                            type="button"
                                            className="btn-icon"
                                            title={u.totp_ativo ? 'Repor verificação em dois passos (telemóvel perdido ou trocado)' : 'A app autenticadora ainda não foi configurada'}
                                            aria-label={`Repor verificação em dois passos de ${u.nome_completo}`}
                                            disabled={!u.totp_ativo || aRepor2fa !== null}
                                            onClick={() => repor2fa(u)}
                                        ><OrcamentoIcon name="shield" fallback="🛡" /></button>}

                                        <button className="btn-icon" title="Editar" onClick={() => handleOpenModal(u)}><OrcamentoIcon name="edit" fallback="✏️" /></button>
                                        <button
                                            className="btn-icon"
                                            title={u.ativo ? 'Desativar' : 'Ativar'}
                                            onClick={() => toggleStatus(u)}
                                        >
                                            <OrcamentoIcon name={u.ativo ? "close" : "check"} fallback={u.ativo ? "🚫" : "✅"} />
                                        </button>
                                        <button
                                            className="btn-icon"
                                            title="Apagar Permanentemente"
                                            onClick={() => handleDelete(u)}
                                            style={{ color: '#e53e3e' }}
                                        >
                                            <OrcamentoIcon name="trash" fallback="🗑️" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <OrcamentoDialog data-admin-dialog="users" onClose={() => setShowModal(false)}>
                    <div className="modal-content admin-modal-card" style={{ maxWidth: '500px' }}>
                        <header className="modal-header admin-modal-header">
                            <h2>{editingUser ? 'Editar Utilizador' : 'Novo Utilizador'}</h2>
                            <button className="btn-close" aria-label="Fechar" onClick={() => setShowModal(false)}><OrcamentoIcon name="close" fallback="×" /></button>
                        </header>
                        <form className="admin-modal-body" onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Nome Completo:</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.nome_completo}
                                    onChange={e => setFormData({ ...formData, nome_completo: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Tipo de Utilizador:</label>
                                <div style={{ display: 'flex', gap: '20px', marginTop: '5px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'normal', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="tipo_utilizador"
                                            value="interno"
                                            checked={formData.tipo_utilizador === 'interno'}
                                            onChange={e => setFormData({ ...formData, tipo_utilizador: e.target.value })}
                                        />
                                        <OrcamentoIcon name="building" fallback="🏠" /> Interno (Acede ao Portal)
                                    </label>
                                    {/* Tipo dado às contas criadas automaticamente ao registar um trabalhador
                                        (routes/trabalhadores.js). Implica sempre o perfil mobile. */}
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'normal', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="tipo_utilizador"
                                            value="mobile"
                                            checked={formData.tipo_utilizador === 'mobile'}
                                            onChange={e => setFormData({ ...formData, tipo_utilizador: e.target.value, perfil_mobile: true })}
                                        />
                                        <OrcamentoIcon name="phone" fallback="📱" /> Mobile (Operador no telemóvel)
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'normal', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="tipo_utilizador"
                                            value="externo"
                                            checked={formData.tipo_utilizador === 'externo'}
                                            onChange={e => setFormData({ ...formData, tipo_utilizador: e.target.value })}
                                        />
                                        <OrcamentoIcon name="users" fallback="🌐" /> Externo (Apenas Dados/Planeamento)
                                    </label>
                                </div>
                            </div>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Username:</label>
                                    <input
                                        type="text"
                                        required
                                        disabled={editingUser}
                                        value={formData.username}
                                        onChange={e => setFormData({ ...formData, username: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Role:</label>
                                    <select
                                        value={formData.role}
                                        onChange={e => setFormData({ ...formData, role: e.target.value })}
                                    >
                                        <option value="user">User</option>
                                        <option value="admin">Admin</option>
                                        <option value="viewer">Viewer</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group checkbox-group" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                                <input
                                    type="checkbox"
                                    id="perfil_mobile"
                                    checked={formData.perfil_mobile || formData.tipo_utilizador === 'mobile'}
                                    disabled={formData.tipo_utilizador === 'mobile'}
                                    onChange={e => setFormData({ ...formData, perfil_mobile: e.target.checked })}
                                    style={{ width: '20px', height: '20px', margin: 0 }}
                                />
                                <label htmlFor="perfil_mobile" style={{ margin: 0, cursor: 'pointer', fontWeight: '500' }}>Perfil Mobile / Operador (Acesso Restrito)</label>
                            </div>
                            <div className="form-group">
                                <label>Email:</label>
                                <input
                                    type="text"
                                    placeholder="Ex: joao@sprod.pt"
                                    value={formData.email || ''}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                />
                                <small>Um email válido permite recuperar a palavra-passe a partir do login.</small>
                            </div>
                            <div className="form-group">
                                <label>Telefone / Telemóvel:</label>
                                <input
                                    type="text"
                                    placeholder="Ex: 912 345 678"
                                    value={formData.telefone}
                                    onChange={e => setFormData({ ...formData, telefone: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>{editingUser ? 'Nova Password (deixe vazio para manter)' : 'Password:'}</label>
                                <div className="password-wrapper">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        required={!editingUser}
                                        autoComplete="new-password"
                                        placeholder={editingUser ? 'Deixe vazio para manter a atual' : ''}
                                        value={formData.password}
                                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                                    />
                                    <button 
                                        type="button" 
                                        className="password-toggle" 
                                        onClick={() => setShowPassword(!showPassword)}
                                        title={showPassword ? "Ocultar" : "Mostrar"}
                                    >
                                        {showPassword ? (
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', opacity: 0.8 }}><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                        ) : (
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', opacity: 0.8 }}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            {formData.role !== 'admin' && (
                            <div className="form-group" style={{ marginTop: '16px' }}>
                                <label style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span>Permissoes de Modulos:</span>
                                    <span style={{ fontSize: '11px', color: '#718096', fontWeight: 400 }}>
                                        {formData.permissoes ? 'Personalizado' : 'Acesso Total'}
                                    </span>
                                </label>
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#4a5568', cursor: 'pointer', margin: 0 }}>
                                            <input
                                                type="checkbox"
                                                checked={!formData.permissoes}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setFormData({ ...formData, permissoes: null });
                                                    } else {
                                                        const all = {};
                                                        ALL_MODULES.forEach(m => { all[m.key] = true; });
                                                        setFormData({ ...formData, permissoes: all });
                                                    }
                                                }}
                                                style={{ width: '16px', height: '16px', margin: 0 }}
                                            />
                                            Acesso total (sem restricoes)
                                        </label>
                                    </div>
                                    {formData.permissoes && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                            {ALL_MODULES.map(mod => (
                                                <label key={mod.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 'normal', cursor: 'pointer', padding: '4px 6px', borderRadius: '4px', background: formData.permissoes[mod.key] !== false ? '#ebf8ff' : '#fff5f5', margin: 0 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.permissoes[mod.key] !== false}
                                                        onChange={(e) => {
                                                            setFormData({
                                                                ...formData,
                                                                permissoes: { ...formData.permissoes, [mod.key]: e.target.checked }
                                                            });
                                                        }}
                                                        style={{ width: '16px', height: '16px', margin: 0 }}
                                                    />
                                                    <LiquidText>{mod.label}</LiquidText>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            )}

                            <div className="modal-actions admin-modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                                <button type="submit" className="btn-primary">Guardar Utilizador</button>
                            </div>
                        </form>
                    </div>
                </OrcamentoDialog>
            )}
        </div>
    );
};

export default function Utilizadores() { return <OrcamentoFeedbackProvider><UtilizadoresContent /></OrcamentoFeedbackProvider>; }
